import { Dispatch, SetStateAction, useState, useEffect, useContext } from 'react';
import './App.css';
import { Constructor } from './Constructor';
import { canvasTextureContext } from './context/canvasTextureContext';
import { canvasBackgroundContext } from './context/canvasBackgroundContext';
import { canvasModeContext } from './context/canvasModeContext';
import { canvasHandFrameDataContext } from './context/canvasHandFrameDataContext';
import { canvasTotalPriceContext } from './context/canvasTotalPriceContext';
import { canvasOptionsContext } from './context/canvasOptionsContext';
import { useTextureData } from './hooks/useTextureData';
import { useBackgroundData } from './hooks/useBackgroundData';
import { useCollectionsData } from './hooks/useCollectionsData';
import { useOptionsData } from './hooks/useOptionsData';
import { ModeNames } from './types/namesList';
import { Converter } from './Converter';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { theme } from './theme';
import { collectionsContext } from "./context/collectionsContext";

const initialHandFrame = { width: 140, height: 220, x: 262, y: 35 };

const domContainer = document.querySelector('#constructor_app'),
    isSimpleMode = domContainer?.getAttribute('data-mode') === 'simpleMode';

function App() {
    const [isConverter, setIsConverter] = useState(false);
    const [initialCollectionsData] = useCollectionsData();
    const [newCollectionsData, setNewCollectionsData] = useState(initialCollectionsData);
    const [initialImgData] = useTextureData(newCollectionsData.elementId);
    const [newImgData, setNewImgData] = useState(initialImgData);
    const [initialBackgroundData] = useBackgroundData(newCollectionsData.elementId, newCollectionsData.sectionId);
    const [newBackgroundData, setNewBackgroundData] = useState(initialBackgroundData);
    const [initialOptionsData] = useOptionsData(newCollectionsData.elementId, newCollectionsData.sectionId);
    const [newOptionsData, setNewOptionsData] = useState(initialOptionsData);
    const [handFrameData, setNewHandFrameData] = useState(initialHandFrame);
    const [totalPrice, setNewTotalPrice] = useState(0);
    const url = new URL(window.location.href);
    // const defaultCategory = url.searchParams.get('category') ?? '';
    // const defaultName = url.searchParams.get('name') ?? '';
    const defaultMode = url.searchParams.get('mode') ?? ModeNames.Handmode;
    const [mode, setNewMode]: [string, Dispatch<SetStateAction<any>>] = useState(defaultMode ? defaultMode : ModeNames.Handmode);
    const defaultIsSeamless = url.searchParams.get('isSeamless') === 'true' || isSimpleMode || newBackgroundData.isSeamless;
    const defaultIsSigned = url.searchParams.get('isSigned') === 'true';
    const defaultIsFlipped = url.searchParams.get('isFlipped') === 'true';
    const getConverter = url.searchParams.get('converter') === 'true';
    const [isSeamless, setIsSeamless]: [boolean, Dispatch<SetStateAction<any>>] = useState(defaultIsSeamless);
    const [isSigned, setIsSigned]: [boolean, Dispatch<SetStateAction<any>>] = useState(defaultIsSigned);
    const [isFlipped, setIsFlipped]: [boolean, Dispatch<SetStateAction<any>>] = useState(defaultIsFlipped);
    // const [defaultCategoryIndex, setDefaultCategoryIndex] = useState<number>(0);
    // const [defaultCollectionIndex, setDefaultCollectionIndex] = useState<number>(0);
    const [fullscreenMode, setFullscreenMode] = useState<boolean>(false);
    const CanvasCollectionsProvider = collectionsContext.Provider;
    const CanvasImgProvider = canvasTextureContext.Provider;
    const CanvasBackgroundProvider = canvasBackgroundContext.Provider;
    const CanvasModeProvider = canvasModeContext.Provider;
    const CanvasHandFrameSizeProvider = canvasHandFrameDataContext.Provider;
    const CanvasTotalPriceProvider = canvasTotalPriceContext.Provider;
    const CanvasOptionsProvider = canvasOptionsContext.Provider;

    useEffect(() => {
        localStorage.removeItem(`sectionData_32`);
        localStorage.removeItem(`sectionData_33`);
        localStorage.removeItem(`sectionData_35`);
        localStorage.removeItem(`sectionData_37`);
    }, []);

    // временный преобразователь данных с почты
    useEffect(() => {
        if(getConverter) setIsConverter(true);
        else setIsConverter(false);
    }, []);

    useEffect(() => {
        const url = new URL(window.location.href),
            paramElementId = url.searchParams.get('itemId'),
            paramSectionId = url.searchParams.get('sectionId');

        setNewCollectionsData({ ...initialCollectionsData,
            elementId: paramElementId ? Number(paramElementId) : newCollectionsData.elementId,
            sectionId: paramSectionId ? Number(paramSectionId) : newCollectionsData.sectionId,
            prevSectionId: newCollectionsData.prevSectionId
        });
    }, [initialCollectionsData]);

    useEffect(() => {
        if(!newOptionsData || newCollectionsData.prevSectionId != newCollectionsData.sectionId) {
            setNewOptionsData(initialOptionsData);
        }

        if(!defaultIsFlipped && newCollectionsData.prevSectionId != newCollectionsData.sectionId) {//Костыль убирающий отзеркаливание при смене коллекции
            setIsFlipped(false);
        }
    }, [initialOptionsData]);

    useEffect(() => {
        const curSectionData = JSON.parse(localStorage!.getItem(`sectionData_${newCollectionsData.sectionId}`)!),
            img= initialImgData.items.find( item => item.id === curSectionData?.texture)?.img;

        if(img) {
            setNewImgData({...initialImgData, img});
        } else {
            setNewImgData(initialImgData);
        }
    }, [initialImgData, newCollectionsData]);

    useEffect(() => {
        setNewBackgroundData({
            ...initialBackgroundData,
            items: initialBackgroundData.items,
        });
    }, [initialBackgroundData]);

    if(!initialCollectionsData || !initialOptionsData || !initialBackgroundData || !initialImgData) return <div></div>;
    if(!newCollectionsData || !newOptionsData || !newBackgroundData || !newImgData) return <div></div>;

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <CanvasCollectionsProvider
                value={{
                    collectionsData: {
                        items: newCollectionsData.items,
                        elementId: newCollectionsData.elementId,
                        sectionId: newCollectionsData.sectionId,
                        prevSectionId: newCollectionsData.prevSectionId,
                    },
                    setCollectionsData: setNewCollectionsData,
                }}>
                <CanvasTotalPriceProvider value={{ price: totalPrice, setTotalPrice: setNewTotalPrice }}>
                <CanvasModeProvider
                    value={{
                        mode: mode,
                        setMode: setNewMode,
                        isSeamless: isSeamless,
                        setIsSeamless: setIsSeamless,
                        isSigned: isSigned,
                        setIsSigned: setIsSigned,
                        isFlipped: isFlipped,
                        setIsFlipped: setIsFlipped,
                        fullscreen: fullscreenMode,
                        setFullscreen: setFullscreenMode,
                    }}
                >
                    <CanvasOptionsProvider value={{ options: newOptionsData, setOptions: setNewOptionsData }}>
                        <CanvasHandFrameSizeProvider
                            value={{ handFrameData: handFrameData, setHandFrameData: setNewHandFrameData }}>
                            <CanvasImgProvider
                                value={{
                                    imgData: {
                                        img: newImgData.img,
                                        price: newImgData.price,
                                        catalog: newImgData.catalog,
                                        items: newImgData.items,
                                        remove_background: newImgData.remove_background,
                                        fullData: newImgData.fullData,
                                    },
                                    setImgData: setNewImgData,
                                }}
                            >
                                <CanvasBackgroundProvider
                                    value={{
                                        backgroundData: {
                                            img: newBackgroundData.img,
                                            backgroundColor: newBackgroundData.backgroundColor,
                                            name: newBackgroundData.name,
                                            price: newBackgroundData.price,
                                            groupName: newBackgroundData.groupName,
                                            isSeamless: newBackgroundData.isSeamless,
                                            isSeamlessMode: newBackgroundData.isSeamlessMode,
                                            items: newBackgroundData.items,
                                        },
                                        setBackgroundData: setNewBackgroundData,
                                    }}
                                >
                                        {initialImgData
                                            && initialCollectionsData
                                            && initialOptionsData
                                            && initialBackgroundData
                                            && !isConverter && (
                                            <div className="App">
                                                <Constructor mode={mode} isSeamless={isSeamless} isSigned={isSigned}/>
                                            </div>
                                        )}
                                        {isConverter && <Converter/>}
                                </CanvasBackgroundProvider>
                            </CanvasImgProvider>
                        </CanvasHandFrameSizeProvider>
                    </CanvasOptionsProvider>
                </CanvasModeProvider>
            </CanvasTotalPriceProvider>
            </CanvasCollectionsProvider>
        </ThemeProvider>
    );
}

export default App;
